import React, { useEffect, useState, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { globalVars } from "../../global/GlobalVars.js";
import DataPicker from "./DataPicker.jsx";
import * as render from "../../utils/render.js";
import { de, enUS, it } from "date-fns/locale";
import * as language from "../../utils/language.js";
import * as rwData from "../../utils/rwData.js";
import * as websocket from "../../utils/communication.js";
import * as XLSX from "xlsx";

//========================================================================
// Konfiguration der api-Schnittstelle
const counterDataUrl =
  window.location.port === "3000"
    ? `http://${window.location.hostname}:${globalVars.httpPort}/counter/`
    : `/api/counter/`;

//========================================================================
// Konstanten
const NUM_COUNTERS = 20;

const EMPTY_SELECTED_COUNTERS = Array.from(
  { length: NUM_COUNTERS + 1 },
  () => ({ id: "" }),
);

const DEFAULT_DATA = { first: 0, last: 0, diff: 0 };

const INTEGER_TYPES = new Set([
  "sint",
  "int",
  "dint",
  "lint",
  "usint",
  "uint",
  "udint",
  "ulint",
  "byte",
  "word",
  "dword",
  "lword",
]);

const localeMap = { de, en: enUS, it };

//========================================================================
// API-Funktionen
//========================================================================
const fetchData = async (ids, startDate, endDate, setDatasets) => {
  // ids hat immer NUM_COUNTERS Einträge (leere bleiben als null erhalten,
  // damit Zeilen-Index == Dataset-Index bleibt)
  const hasAnyId = ids.some(Boolean);
  if (!hasAnyId) {
    setDatasets(Array(NUM_COUNTERS).fill(null));
    return;
  }

  try {
    // Für jede ID: bei leereer ID sofort null zurückgeben, sonst API-Call
    const results = await Promise.all(
      ids.map((id) => {
        if (!id) return Promise.resolve(null);
        return axios
          .get(counterDataUrl + "data", {
            params: {
              startDateString: startDate.toISOString(),
              endDateString: endDate.toISOString(),
              id,
              resolution: 0, // ersten und letzten Zählerstand und Differenz
            },
          })
          .catch(() => null);
      }),
    );

    // Positionen bleiben 1:1 erhalten — null für leere/fehlerhafte Zeilen
    const allData = results.map((response, index) => {
      if (!response) return null;
      return {
        data: response.data.data ?? DEFAULT_DATA,
        info: {
          id: response.data.id,
          name: response.data.name || `Dataset ${index + 1}`,
          unit: response.data.unit || "",
          decimalPoints: response.data.decimalPoints || 0,
          type: response.data.type || "",
        },
      };
    });

    setDatasets(allData);
  } catch (error) {
    console.error("Fehler beim Abrufen der Daten:", error);
  }
};

//========================================================================
// Liste aller aufgezeichneten Zähler anfordern
const fetchAllCounters = async (setAllCounters) => {
  try {
    const response = await axios.get(counterDataUrl + "allCounters");
    setAllCounters(response.data);
  } catch (error) {
    console.error("Fehler beim Abrufen der Zähler:", error);
  }
};

//========================================================================
// Zähler speichern
const saveCounter = async (
  id,
  selectedCounters,
  setSaveName,
  setSavedCounters,
) => {
  if (!id.trim()) return;

  const counterData = {
    id,
    resolution: 0,
    ...Object.fromEntries(
      Array.from({ length: NUM_COUNTERS }, (_, i) => [
        `idCounter${i + 1}`,
        selectedCounters[i + 1]?.id ?? "",
      ]),
    ),
  };

  try {
    await axios.post(counterDataUrl + "saveCounters", { counterData });
    await getSavedCountersList(setSavedCounters);
    setSaveName("");
  } catch (error) {
    console.error("Fehler beim Speichern der Zähler:", error);
  }
};

//========================================================================
// Anforderung Liste der gespeicherten Zähler
const getSavedCountersList = async (setSavedCounters) => {
  try {
    const response = await axios.get(counterDataUrl + "savedCountersList");
    setSavedCounters(response.data);
  } catch (error) {
    console.error("Fehler beim Abrufen der gespeicherten Zähler:", error);
  }
};

//========================================================================
// Anforderung gespeicherte Zähler
const getSavedCounters = async (event, setSelectedCounters, setSelectedId) => {
  setSelectedCounters(EMPTY_SELECTED_COUNTERS);
  const selectedValue = event.target.value;
  setSelectedId(selectedValue);

  if (!selectedValue) return;

  try {
    const response = await axios.get(counterDataUrl + "savedCounters", {
      params: { counterName: selectedValue },
    });

    const loaded = [
      { id: "" },
      ...Array.from({ length: NUM_COUNTERS }, (_, i) => ({
        id: response.data[`idCounter${i + 1}`] ?? "",
      })),
    ];

    setSelectedCounters(loaded);
  } catch (error) {
    console.error("Fehler beim Abrufen der gespeicherten Zähler:", error);
  }
};

//========================================================================
// gespeicherte Zähler löschen
const deleteSavedCounters = async (id, setSavedCounters) => {
  if (!id) return;
  try {
    await axios.post(counterDataUrl + "deleteCounters", { counterName: id });
    await getSavedCountersList(setSavedCounters);
  } catch (error) {
    console.error("Fehler beim Löschen der gespeicherten Zähler:", error);
  }
};

//========================================================================
// Hilfsfunktion: Aktuellen Anzeigewert berechnen
const computeActualValue = (rawValue, info) => {
  if (rawValue === undefined || rawValue === null || rawValue === "") return "";
  const type = info.type?.toLowerCase() ?? "";
  const dp = info.decimalPoints ?? 0;
  let numeric;

  if (INTEGER_TYPES.has(type)) {
    numeric = (rawValue / 10 ** dp).toFixed(dp);
  } else if (type === "real") {
    numeric = Number(rawValue).toFixed(dp);
  } else {
    return "";
  }

  return numeric.replace(".", ",") + (info.unit ? ` ${info.unit}` : "");
};

//========================================================================
// Zählerstand formattieren
const formatCounterValue = (value, info) => {
  if (value === undefined || value === null || value === "") return "";
  const dp = info?.decimalPoints ?? 0;
  const unit = info?.unit ?? "";
  return Number(value).toFixed(dp).replace(".", ",") + (unit ? ` ${unit}` : "");
};

//========================================================================
// Komponente Zählerstand
const Zählerstand = observer((props) => {
  const [datasets, setDatasets] = useState([]);
  const [actualValues, setActualValues] = useState({});
  const [allCounters, setAllCounters] = useState([]);
  const [savedCounters, setSavedCounters] = useState([]);
  const [selectedCounters, setSelectedCounters] = useState(
    EMPTY_SELECTED_COUNTERS,
  );
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 2 * 60 * 60 * 1000),
  );
  const [endDate, setEndDate] = useState(new Date());
  const [saveName, setSaveName] = useState("");
  const [selectedId, setSelectedId] = useState("");

  //========================================================================
  // Sprachumschaltung
  const currentLocale = localeMap[globalVars.language] ?? de;

  //========================================================================
  // Zeitauswahl begrenzen
  useEffect(() => {
    const now = new Date();
    if (startDate > endDate) setStartDate(endDate);
    if (endDate > now) setEndDate(now);
  }, [startDate, endDate]);

  //========================================================================
  // Daten laden wenn Zählerauswahl oder Zeitraum sich ändert
  useEffect(() => {
    const ids = Array.from(
      { length: NUM_COUNTERS },
      (_, i) => selectedCounters[i + 1].id,
    );
    fetchData(ids, startDate, endDate, setDatasets);
  }, [selectedCounters, startDate, endDate]);

  //========================================================================
  // Websocket-Registrierung für Echtzeit-Werte
  useEffect(() => {
    datasets.forEach((dataset) => {
      if (!dataset) return;
      const id = dataset?.info?.id;
      if (!id || globalVars.reqDataValues.includes(id)) return;

      const newReqDataValues = [...globalVars.reqDataValues, id];
      globalVars.setReqDataValues(newReqDataValues);
      websocket.registerDataPoints(globalVars.ws, newReqDataValues, "register");
    });
  }, [datasets]);

  //========================================================================
  // Aktualwerte aus globalVars lesen und in actualValues-Objekt schreiben
  const datasetIds = datasets.map((d) => d?.info?.id).join(",");

  useEffect(() => {
    if (datasets.length === 0) return;

    setActualValues(
      datasets.reduce((acc, dataset) => {
        if (!dataset) return acc;
        const id = dataset.info?.id;
        if (!id) return acc;
        try {
          const rawValue = rwData.readRcvValue(id)?.value;
          acc[id] = computeActualValue(rawValue, dataset.info);
        } catch {
          acc[id] = "";
        }
        return acc;
      }, {}),
    );
  }, [globalVars.rcvValues, datasetIds]);

  //========================================================================
  // Initiales Laden
  useEffect(() => {
    fetchAllCounters(setAllCounters);
    getSavedCountersList(setSavedCounters);
  }, []);

  //========================================================================
  // Hilfsfunktion: skalierte Position
  const s = (val) => render.parseCssValue(val, globalVars.scaleFactor);

  // Zeilenpositionen für Auswahl und Anzeige
  const rowTop = (i) => s(`${100 + (i - 1) * 40}px`);

  // Zeilenpositionen für Grid-Lines
  const rowTopGrid = (i) => s(`${55 + (i - 1) * 40}px`);

  //========================================================================
  // Zähler für Download formattieren
  const convertToCSV = (arr, headers) => {
    try {
      const array = [headers].concat(arr.map((item) => Object.values(item)));

      return array
        .map((it) => {
          return it.join("\t");
        })
        .join("\n");
    } catch (err) {
      // console.log(err);
      return [];
    }
  };

  const downloadCSV = (jsonArray, headers) => {
    try {
      const csv = convertToCSV(jsonArray, headers);
      const bom = "\uFEFF";
      const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download =
        language.displayText(["Zähler", "contatore", "counter"]) + ".csv";

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (err) {
      // console.error("CSV Download Fehler:", err);
    }
  };

  const downloadXLSX = (jsonArray, headers, headerKeys) => {
    try {
      const ws = XLSX.utils.json_to_sheet(jsonArray, { header: headerKeys });
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        language.displayText(["Zähler", "contatore", "counter"]),
      );

      XLSX.writeFile(
        wb,
        language.displayText(["Zähler", "contatore", "counter"]) + ".xlsx",
      );
    } catch (err) {
      // console.log(err);
    }
  };

  //========================================================================
  // Zähler downloaden
  const downloadCounter = async (mode) => {
    try {
      // Null-Einträge herausfiltern
      const validDatasets = datasets.filter((d) => d !== null);

      // Daten für den Download aufbereiten
      const countersToDownload = validDatasets.map((dataset) => ({
        counter: language.displayText(dataset.info.name),
        actual: actualValues[dataset?.info?.id] ?? "",
        start: formatCounterValue(dataset?.data?.first, dataset?.info),
        end: formatCounterValue(dataset?.data?.last, dataset?.info),
        difference: formatCounterValue(dataset?.data?.diff, dataset?.info),
        startDate:
          startDate.toLocaleDateString() +
          ", " +
          startDate.toLocaleTimeString(),
        endDate:
          endDate.toLocaleDateString() + ", " + endDate.toLocaleTimeString(),
      }));

      const rowTitles = [
        language.displayText(["Zähler", "contatore", "counter"]),
        language.displayText([
          "Zählerstand aktuell",
          "lettura attuale",
          "reading actual",
        ]),
        language.displayText([
          "Zählerstand Start",
          "lettura start",
          "reading start",
        ]),
        language.displayText([
          "Zählerstand Ende",
          "lettura fine",
          "reading end",
        ]),
        language.displayText(["Differenz", "differenza", "difference"]),
        language.displayText([
          "Zeitpunkt Start",
          "ora di inizio",
          "start time",
        ]),
        language.displayText(["Zeitpunkt Ende", "ora di fine", "end time"]),
      ];

      const headerKeys = ["counter", "actual", "start", "end", "difference"];

      if (mode === "csv") {
        downloadCSV(countersToDownload, rowTitles);
      } else if (mode === "xlsx") {
        downloadXLSX(countersToDownload, rowTitles, headerKeys);
      }
    } catch (error) {
      console.error("Error during download preparation:", error);
    }
  };

  //========================================================================
  // Grafische Darstellung
  return (
    <div>
      {/* schwarzer Hintergrund */}
      <div
        style={{
          position: "absolute",
          top: s("45px"),
          left: s("10px"),
          height: s("861px"),
          width: s("1900px"),
          backgroundColor: "#0d1018",
        }}
      />
      {/* Spaltenüberschrift */}
      <div
        style={{
          position: "absolute",
          top: s("55px"),
          left: s("20px"),
          height: s("40px"),
          width: s("1450px"),
          backgroundColor: "#2d323f",
        }}
      />
      {[
        {
          left: "10px",
          width: "705px",
          label: language.displayText(["Zähler", "fine", "endtime"]),
        },
        {
          left: "730px",
          width: "183px",
          label: language.displayText([
            "Zählerstand aktuell",
            "fine",
            "endtime",
          ]),
        },
        {
          left: "915px",
          width: "183px",
          label: language.displayText(["Zählerstand Start", "fine", "endtime"]),
        },
        {
          left: "1100px",
          width: "183px",
          label: language.displayText(["Zählerstand Ende", "fine", "endtime"]),
        },
        {
          left: "1285px",
          width: "183px",
          label: language.displayText(["Differenz", "fine", "endtime"]),
        },
      ].map(({ left, width, label }) => (
        <React.Fragment key={left}>
          <div
            style={{
              position: "absolute",
              top: s("60px"),
              left: s(left),
              height: s("35px"),
              width: s(width),
              fontSize: s("16px"),
              backgroundColor: "transparent",
              color: "#a9b1bd",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {label}
          </div>
        </React.Fragment>
      ))}

      {/* Grid-Lines */}
      {Array.from({ length: NUM_COUNTERS + 2 }, (_, i) => {
        const idx = i + 1;
        return (
          <React.Fragment key={idx}>
            <div
              style={{
                position: "absolute",
                top: rowTopGrid(idx),
                left: s("20px"),
                height: s("1px"),
                width: s("1450px"),
                backgroundColor: "#474a52",
              }}
            ></div>
          </React.Fragment>
        );
      })}
      {[
        {
          left: "19px",
        },
        {
          left: "730px",
        },
        {
          left: "915px",
        },
        {
          left: "1100px",
        },
        {
          left: "1285px",
        },
        {
          left: "1470px",
        },
      ].map(({ left }) => (
        <React.Fragment key={left}>
          <div
            style={{
              position: "absolute",
              top: s("55px"),
              left: s(left),
              width: s("1px"),
              height: s("840px"),
              backgroundColor: "#474a52",
            }}
          ></div>
        </React.Fragment>
      ))}

      {/* DataPicker und Anzeigen */}
      {Array.from({ length: NUM_COUNTERS }, (_, i) => {
        const idx = i + 1;
        const dataset = datasets[i];
        const outputStyle = {
          position: "absolute",
          top: rowTop(idx),
          height: s("30px"),
          width: s("185px"),
          fontSize: s("14px"),
          backgroundColor: "#3d7aca00",
          // backgroundColor: "#3d7aca6c",
          color: "#a9b1bd",
          display: "flex",
          overflow: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          justifyContent: "center",
          alignItems: "center",
        };

        return (
          <React.Fragment key={idx}>
            <DataPicker
              index={idx}
              style={{
                top: rowTop(idx),
                left: s("20px"),
                height: s("30px"),
                width: s("920px"),
                fontSize: s("14px"),
              }}
              allCounters={allCounters}
              selectedCounters={selectedCounters}
              setselectedCounters={setSelectedCounters}
            />
            <output style={{ ...outputStyle, left: s("730px") }}>
              {actualValues[dataset?.info?.id] ?? ""}
            </output>
            <output style={{ ...outputStyle, left: s("915px") }}>
              {formatCounterValue(dataset?.data?.first, dataset?.info)}
            </output>
            <output style={{ ...outputStyle, left: s("1100px") }}>
              {formatCounterValue(dataset?.data?.last, dataset?.info)}
            </output>
            <output style={{ ...outputStyle, left: s("1285px") }}>
              {formatCounterValue(dataset?.data?.diff, dataset?.info)}
            </output>
          </React.Fragment>
        );
      })}

      {/* Zeitauswahl */}
      <div
        style={{
          position: "absolute",
          top: s("55px"),
          left: s("1500px"),
          height: s("130px"),
          width: s("400px"),
          backgroundColor: "#2d323f",
          borderTop: "1px solid #1e2330",
          borderRadius: s("6px"),
          padding: s("12px"),
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: s("6px"),
        }}
      >
        <div
          style={{
            fontSize: s("16px"),
            fontWeight: 600,
            letterSpacing: "0.14em",
            color: "#a9b1bd",
            paddingBottom: s("4px"),
            borderBottom: "1px solid #1e2a3a",
            marginBottom: s("2px"),
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          {language.displayText(["Zeitraum", "Periodo", "Time Range"])}
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: s("103px"),
          left: s("1560px"),
          height: s("28px"),
          width: s("50px"),
          fontSize: s("14px"),
          color: "#a9b1bd",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {language.displayText(["Start", "Inizio", "Start"])}
      </div>

      <div
        style={{
          position: "absolute",
          top: s("141px"),
          left: s("1560px"),
          height: s("28px"),
          width: s("50px"),
          fontSize: s("14px"),
          color: "#a9b1bd",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {language.displayText(["Ende", "Fine", "End"])}
      </div>

      <div
        style={{
          position: "absolute",
          top: s("102px"),
          left: s("1550px"),
          width: "21%",
        }}
      >
        <DatePicker
          selected={startDate}
          onChange={setStartDate}
          showTimeSelect
          timeIntervals={15}
          timeCaption={language.displayText(["Zeit", "tempo", "time"])}
          timeFormat="HH:mm"
          dateFormat="dd.MM.yyyy HH:mm"
          locale={currentLocale}
          customInput={
            <input
              style={{
                position: "relative",
                left: s("85px"),
                width: s("200px"),
                height: s("26px"),
                background: "#0f1117",
                border: "1px solid #2a3448",
                borderRadius: s("6px"),
                color: "#cbd5e1",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: s("14px"),
                textAlign: "center",
                outline: "none",
                cursor: "pointer",
              }}
            />
          }
        />
      </div>
      <div
        style={{
          position: "absolute",
          top: s("140px"),
          left: s("1550px"),
          width: "21%",
        }}
      >
        <DatePicker
          selected={endDate}
          onChange={setEndDate}
          showTimeSelect
          timeIntervals={15}
          timeCaption={language.displayText(["Zeit", "tempo", "time"])}
          timeFormat="HH:mm"
          dateFormat="dd.MM.yyyy HH:mm"
          locale={currentLocale}
          customInput={
            <input
              style={{
                position: "relative",
                left: s("85px"),
                width: s("200px"),
                height: s("26px"),
                background: "#0f1117",
                border: "1px solid #2a3448",
                borderRadius: s("6px"),
                color: "#cbd5e1",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: s("14px"),
                textAlign: "center",
                outline: "none",
                cursor: "pointer",
              }}
            />
          }
        />
      </div>

      {/* Gruppen speichern/laden/löschen */}
      <div
        style={{
          position: "absolute",
          top: s("195px"),
          left: s("1500px"),
          height: s("130px"),
          width: s("400px"),
          backgroundColor: "#2d323f",
          borderTop: "1px solid #1e2330",
          borderRadius: s("6px"),
          padding: s("12px"),
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: s("8px"),
        }}
      >
        <div
          style={{
            fontSize: s("16px"),
            fontWeight: 600,
            letterSpacing: "0.14em",
            color: "#a9b1bd",
            paddingBottom: s("4px"),
            borderBottom: "1px solid #1e2a3a",
            marginBottom: s("2px"),
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          {language.displayText(["Gruppen", "gruppi", "groups"])}
        </div>
        <div style={{ display: "flex", gap: s("8px"), alignItems: "center" }}>
          <select
            style={{
              background: "#0f1117",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("14px"),
              padding: `0 ${s("10px")}`,
              height: s("30px"),
              width: s("260px"),
              outline: "none",
              cursor: "pointer",
            }}
            onChange={(e) =>
              getSavedCounters(e, setSelectedCounters, setSelectedId)
            }
            value={selectedId}
          >
            <option value=""></option>
            {savedCounters.map((item) => (
              <option key={item.id ?? item} value={item.id ?? item}>
                {item}
              </option>
            ))}
          </select>
          <button
            style={{
              background: "#f8717108",
              border: "1px solid #f8717122",
              borderRadius: s("6px"),
              color: "#f87171",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("14px"),
              fontWeight: 500,
              width: s("100px"),
              height: s("30px"),
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={() => deleteSavedCounters(selectedId, setSavedCounters)}
          >
            {language.displayText(["Löschen", "cancella", "delete"])}
          </button>
        </div>
        <div style={{ display: "flex", gap: s("8px"), alignItems: "center" }}>
          <input
            type="text"
            style={{
              background: "#0f1117",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("14px"),
              padding: `0 ${s("10px")}`,
              height: s("30px"),
              width: s("260px"),
              outline: "none",
              boxSizing: "border-box",
            }}
            onChange={(e) => setSaveName(e.target.value)}
            value={saveName}
            placeholder={language.displayText([
              "neue Zählergruppe",
              "nuovo gruppo",
              "new group",
            ])}
          />
          <button
            style={{
              background: "#00d4ff0f",
              border: "1px solid #00d4ff33",
              borderRadius: s("6px"),
              color: "#00d4ff",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("14px"),
              fontWeight: 500,
              width: s("100px"),
              height: s("30px"),
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={() =>
              saveCounter(
                saveName,
                selectedCounters,
                setSaveName,
                setSavedCounters,
              )
            }
          >
            {language.displayText(["Speichern", "Salva", "Save"])}
          </button>
        </div>
      </div>

      {/* Download */}
      <div
        style={{
          position: "absolute",
          top: s("335px"),
          left: s("1500px"),
          width: s("400px"),
          backgroundColor: "#2d323f",
          border: "1px solid #1e2330",
          borderRadius: s("6px"),
          padding: s("12px"),
          display: "flex",
          flexDirection: "column",
          gap: s("8px"),
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            fontSize: s("13px"),
            fontWeight: 600,
            letterSpacing: "0.12em",
            color: "#a9b1bd",
            borderBottom: "1px solid #1e2a3a",
            paddingBottom: s("6px"),
            textAlign: "center",
          }}
        >
          {language.displayText(["Download", "Download", "Download"])}
        </div>
        <div style={{ display: "flex", gap: s("8px") }}>
          <button
            style={{
              flex: 1,
              height: s("34px"),
              fontSize: s("13px"),
              backgroundColor: "#1e2330",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              cursor: "pointer",
            }}
            onClick={() => downloadCounter("csv")}
          >
            CSV
          </button>
          <button
            style={{
              flex: 1,
              height: s("34px"),
              fontSize: s("13px"),
              backgroundColor: "#1e2330",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              cursor: "pointer",
            }}
            onClick={() => downloadCounter("xlsx")}
          >
            Excel
          </button>
        </div>
      </div>
    </div>
  );
});

export default Zählerstand;
